import type { FitBoundsOptions, LngLatBoundsLike, Map as MapboxMap, PaddingOptions } from 'mapbox-gl'

import { createMapBounds, isCoordinateWithinBounds } from './bounds'
import type { MapBounds } from './types'

type SearchMapCoordinate = {
  latitude: number
  longitude: number
}

type LngLatTuple = [number, number]
type LngLatTupleBounds = [LngLatTuple, LngLatTuple]

/**
 * Converts API offer-list bounds into the tuple shape accepted by Mapbox camera APIs.
 */
export function parseOfferListBounds(bounds: App.OfferListBounds) {
  return [
    [+bounds.southwest.lng, +bounds.southwest.lat],
    [+bounds.northeast.lng, +bounds.northeast.lat],
  ] as LngLatBoundsLike
}

function isLngLatTupleBounds(bounds: LngLatBoundsLike): bounds is LngLatTupleBounds {
  return Array.isArray(bounds) &&
    bounds.length === 2 &&
    Array.isArray(bounds[0]) &&
    Array.isArray(bounds[1]) &&
    bounds[0].length === 2 &&
    bounds[1].length === 2
}

function unwrapAntimeridianBoundsForFit(bounds: LngLatBoundsLike): LngLatBoundsLike {
  if (!isLngLatTupleBounds(bounds)) {
    return bounds
  }

  const [[west, south], [east, north]] = bounds

  // Mapbox fitBounds/cameraForBounds expects the east edge to be numerically to
  // the right of west. For wrapped destinations like Fiji we preserve the small
  // cross-antimeridian span by unwrapping east into the next world copy.
  if (east >= west) {
    return bounds
  }

  return [[west, south], [east + 360, north]]
}

/**
 * Applies a caller-provided zoom offset while keeping the result within the map's min/max zoom.
 */
export function getAdjustedZoom(
  map: MapboxMap,
  zoom: number,
  cameraZoomAdjustment: number,
) {
  return Math.max(
    map.getMinZoom(),
    Math.min(zoom + cameraZoomAdjustment, map.getMaxZoom()),
  )
}

/**
 * Fits bounds directly, or recomputes the target camera when an extra zoom adjustment is needed.
 */
export function fitBoundsOnMap(
  map: MapboxMap | null,
  bounds: LngLatBoundsLike,
  options: FitBoundsOptions,
  cameraZoomAdjustment: number,
) {
  if (!map) {
    return
  }

  const mapboxBounds = unwrapAntimeridianBoundsForFit(bounds)

  if (cameraZoomAdjustment === 0) {
    map.fitBounds(mapboxBounds, options)
    return
  }

  const camera = map.cameraForBounds(mapboxBounds, options)

  if (!camera?.center) {
    map.fitBounds(mapboxBounds, options)
    return
  }

  map.easeTo({
    center: camera.center,
    zoom: getAdjustedZoom(map, camera.zoom ?? map.getZoom(), cameraZoomAdjustment),
    duration: options?.duration ?? 0,
  })
}

/**
 * Fits all markers into view, using a single-property zoom path when there is only one marker.
 */
export function fitMarkersOnMap(
  map: MapboxMap | null,
  markers: Array<SearchMapCoordinate>,
  padding?: number | PaddingOptions,
  cameraZoomAdjustment = 0,
) {
  if (!map || markers.length === 0) {
    return
  }

  if (markers.length === 1) {
    map.easeTo({
      center: [markers[0].longitude, markers[0].latitude],
      zoom: getAdjustedZoom(map, 16, cameraZoomAdjustment),
      duration: 0,
    })
    return
  }

  const bounds = createMapBounds(markers.map(marker => ({
    lat: marker.latitude,
    lng: marker.longitude,
  })))

  if (!bounds) {
    return
  }

  fitBoundsOnMap(
    map,
    [[bounds.west, bounds.south], [bounds.east, bounds.north]],
    { padding, duration: 0 },
    cameraZoomAdjustment,
  )
}

/**
 * Recenters on a selected marker and optionally applies the standard detail zoom level.
 */
export function centerAndZoomOnMarker(
  map: MapboxMap | null,
  marker: SearchMapCoordinate,
  shouldZoom: boolean,
  cameraZoomAdjustment = 0,
) {
  if (!map) {
    return
  }

  map.easeTo({
    center: [marker.longitude, marker.latitude],
    zoom: shouldZoom ? getAdjustedZoom(map, 16, cameraZoomAdjustment) : map.getZoom(),
    duration: 0,
  })
}

/**
 * Re-centers a marker higher in the mobile viewport so the bottom card does not obscure it.
 */
export function centrePinForMobile(
  map: MapboxMap | null,
  marker: SearchMapCoordinate,
) {
  if (!map) {
    return
  }

  map.easeTo({
    center: [marker.longitude, marker.latitude],
    duration: 0,
  })

  const mapHeight = map.getContainer()?.offsetHeight

  if (!mapHeight) {
    return
  }

  const percentFromTop = 0.25
  const targetPosition = mapHeight * percentFromTop
  const offsetRatio = (mapHeight / 2 - targetPosition) / mapHeight

  map.panBy([0, offsetRatio * mapHeight], { duration: 0 })
}

/**
 * Checks whether any search marker is currently visible inside the live map bounds.
 */
export function mapHasAnyMarkersInView(
  map: MapboxMap | null,
  markers: Array<SearchMapCoordinate>,
): boolean {
  const bounds = map?.getBounds()

  if (!bounds) {
    return false
  }

  return markers.some(marker => bounds.contains([marker.longitude, marker.latitude]))
}

/**
 * Uses neutral bounds logic so marker filtering stays correct for wrapped antimeridian bounds.
 */
export function isMarkerWithinBounds(
  marker: SearchMapCoordinate,
  bounds: MapBounds,
): boolean {
  return isCoordinateWithinBounds(
    { lat: marker.latitude, lng: marker.longitude },
    bounds,
  )
}

export function isTargetPropertySearchOffer(offerMetaData?: App.OfferListMetaData) {
  return offerMetaData?.isMatchingProperty === true
}

/** True when search stream meta marks the offer unavailable (unless property-matched). */
export function isSearchStreamUnavailable(offerMetaData?: App.OfferListMetaData) {
  return offerMetaData?.available === false && !isTargetPropertySearchOffer(offerMetaData)
}

/**
 * Hides search-unavailable offers using stream meta (`available`), not public-offer
 * `isSoldOut`. Property-matched unavailable offers stay visible.
 */
export function shouldDisplayOffer(offerMetaData?: App.OfferListMetaData) {
  return !isSearchStreamUnavailable(offerMetaData)
}
